/**
 * ⭐⭐ O que o ato fez com o bem, na tela — a lista dele, 27/10/2026 (PRD §65).
 *
 * ⚠️ **Espelho de `api/src/lib/efeito-do-ato.ts`**, na mesma ordem; quem decide
 * o que vai ao documento é o servidor, e `npm run testar-fichas-de-imoveis`
 * confere que os dois lados dizem o mesmo. Cópia sem conferência diverge na
 * primeira correção.
 *
 * ⚠️ `grupo: null` é a lista antiga: fora da lista para lançamento novo, e
 * ainda mostrada quando o lançamento já a tem.
 */
import type { EfeitoPatrimonialDoAto } from './api';

export type DestinoDoBem = 'entrou' | 'saiu' | 'a_conferir' | 'onerado' | 'constricao' | 'neutro';

export const EFEITOS_DO_ATO: Array<{ chave: EfeitoPatrimonialDoAto; rotulo: string; bem: DestinoDoBem; grupo: string | null }> = [
  { chave: 'aquisicao', rotulo: 'Aquisição patrimonial', bem: 'entrou', grupo: null },
  { chave: 'transmissao', rotulo: 'Transmissão patrimonial', bem: 'saiu', grupo: null },
  { chave: 'partilha_sucessao', rotulo: 'Partilha ou sucessão', bem: 'saiu', grupo: null },
  { chave: 'constituicao_de_onus', rotulo: 'Constituição de ônus ou garantia', bem: 'onerado', grupo: null },
  { chave: 'cessao_de_direitos', rotulo: 'Cessão de direitos', bem: 'saiu', grupo: null },
  { chave: 'operacao_financeira', rotulo: 'Operação financeira ou obrigacional', bem: 'neutro', grupo: null },
  { chave: 'sem_efeito_identificado', rotulo: 'Sem efeito patrimonial identificado', bem: 'neutro', grupo: null },
  { chave: 'compra_ou_incorporacao', rotulo: 'Compra ou incorporação', bem: 'entrou', grupo: 'Aquisição' },
  { chave: 'venda_doacao_ou_transferencia', rotulo: 'Venda, doação ou transferência', bem: 'a_conferir', grupo: 'Transmissão' },
  { chave: 'heranca', rotulo: 'Herança', bem: 'a_conferir', grupo: 'Sucessão e partilha' },
  { chave: 'inventario', rotulo: 'Inventário', bem: 'a_conferir', grupo: 'Sucessão e partilha' },
  { chave: 'divorcio_ou_partilha', rotulo: 'Divórcio ou partilha', bem: 'a_conferir', grupo: 'Sucessão e partilha' },
  { chave: 'hipoteca_alienacao_fiduciaria_ou_penhor', rotulo: 'Hipoteca, alienação fiduciária ou penhor', bem: 'onerado', grupo: 'Garantias' },
  { chave: 'liberacao_de_garantia_ou_restricao', rotulo: 'Liberação de garantia ou restrição', bem: 'neutro', grupo: 'Garantias' },
  { chave: 'transferencia_de_direitos', rotulo: 'Transferência de direitos patrimoniais ou creditórios', bem: 'a_conferir', grupo: 'Direitos e obrigações' },
  { chave: 'emprestimo_ou_confissao_de_divida', rotulo: 'Empréstimo, confissão de dívida e obrigações', bem: 'neutro', grupo: 'Direitos e obrigações' },
  { chave: 'aporte_em_sociedade', rotulo: 'Aporte de bens ou direitos em sociedade', bem: 'a_conferir', grupo: 'Sociedades' },
  { chave: 'restituicao_aos_socios', rotulo: 'Restituição de ativos aos sócios', bem: 'a_conferir', grupo: 'Sociedades' },
  { chave: 'distribuicao_aos_socios', rotulo: 'Distribuição de lucros ou bens aos sócios', bem: 'a_conferir', grupo: 'Sociedades' },
  { chave: 'incorporacao_societaria', rotulo: 'Incorporação', bem: 'a_conferir', grupo: 'Sociedades' },
  { chave: 'fusao', rotulo: 'Fusão', bem: 'a_conferir', grupo: 'Sociedades' },
  { chave: 'cisao_ou_transformacao', rotulo: 'Cisão ou transformação', bem: 'a_conferir', grupo: 'Sociedades' },
  { chave: 'quotas_ou_acoes', rotulo: 'Entrada, saída ou transferência de quotas ou ações', bem: 'a_conferir', grupo: 'Sociedades' },
  { chave: 'penhora', rotulo: 'Penhora', bem: 'constricao', grupo: 'Constrições judiciais' },
  { chave: 'arresto_sequestro_ou_indisponibilidade', rotulo: 'Arresto, sequestro ou indisponibilidade', bem: 'constricao', grupo: 'Constrições judiciais' },
  { chave: 'cancelamento_de_penhora', rotulo: 'Cancelamento de penhora ou liberação judicial', bem: 'neutro', grupo: 'Constrições judiciais' },
  { chave: 'troca_de_bens', rotulo: 'Troca de bens ou direitos', bem: 'a_conferir', grupo: 'Permuta e direitos reais' },
  { chave: 'usufruto', rotulo: 'Usufruto', bem: 'a_conferir', grupo: 'Permuta e direitos reais' },
  { chave: 'superficie_servidao_ou_outros', rotulo: 'Superfície, servidão ou outros direitos', bem: 'a_conferir', grupo: 'Permuta e direitos reais' },
  { chave: 'cancelamento_de_direito', rotulo: 'Cancelamento ou término de direito constituído', bem: 'neutro', grupo: 'Permuta e direitos reais' },
  { chave: 'retificacao', rotulo: 'Retificação', bem: 'neutro', grupo: 'Registro' },
  { chave: 'encerramento_de_matricula', rotulo: 'Encerramento de matrícula', bem: 'a_conferir', grupo: 'Registro' },
  { chave: 'sem_repercussao', rotulo: 'Ato sem repercussão patrimonial identificada', bem: 'neutro', grupo: 'Sem efeito identificado' },
  { chave: 'informacoes_insuficientes', rotulo: 'Informações insuficientes para determinar o efeito', bem: 'a_conferir', grupo: 'Sem efeito identificado' },
];

/**
 * Na escritura, o papel do investigado decide o lado — espelho de `destinoDoBem`
 * do servidor, conferido pelo teste em todas as combinações.
 */
const DECIDE_PELO_PAPEL: ReadonlySet<EfeitoPatrimonialDoAto> = new Set([
  'venda_doacao_ou_transferencia',
  'transferencia_de_direitos',
  'troca_de_bens',
]);
const ENTREGA: ReadonlySet<string> = new Set(['vendedor', 'doador', 'cedente']);
const RECEBE: ReadonlySet<string> = new Set(['comprador', 'donatario', 'cessionario']);

export function destinoDoBem(efeito: EfeitoPatrimonialDoAto, papelDoInvestigado: string | null): DestinoDoBem {
  const bem = EFEITOS_DO_ATO.find((x) => x.chave === efeito)!.bem;
  if (!DECIDE_PELO_PAPEL.has(efeito) || !papelDoInvestigado) return bem;
  if (ENTREGA.has(papelDoInvestigado)) return 'saiu';
  if (RECEBE.has(papelDoInvestigado)) return 'entrou';
  return bem;
}
